import React, { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { FaCopy, FaExternalLinkAlt, FaTrash, FaSyncAlt } from "react-icons/fa";
import { getUserDetails, getSessionId, isAdminUser } from "../../../../services/authService";
import { getCustomerLinks, updateCustomerLinkOrdersCount, deleteCustomerLink, getLdsDrafts, deleteLdsDraft, updateLdsDraftStatus } from "../../../../services/fhpService";
import { showSuccessToast, errorMessage } from "../../../../services/swalService";
import { useAppContext } from "../../../../contexts/AppContext";
import { totalUsdFromIls } from "../../../../utils/priceDisplaySSOT";
import translateLDSDestinations from "../../../../services/translateLDSDestinations";
import { buildPublicFlightOrDynamicOfferUrl } from "../../../../utils/publicOfferPaths";
import { resolvePublicBaseUrl } from "../../../../utils/resolvePublicBaseUrl";
import { organizedTourBookingPath } from "../../../../modules/organizedTours/constants/routes";
import { tokenToPublicOfferUrl } from "../../../../modules/organizedTours/utils/organizedTourPublicQuoteUrl";
import { buildOrganizedTourDraftRow } from "../../../../modules/organizedTours/utils/buildOrganizedTourDraftRow";
import "./CustomerLinksTable.scss";

const PAGE_SIZE = 50;
const MAX_PAGES = 10;

const LINK_FIELD_NAMES = [
  "public_link",
  "link",
  "url",
  "href",
  "customer_link_url",
  "checkout_url",
  "site_url",
  "draft_url",
  "profile_url",
];

function normalizeDraftAppPath(pathname = "") {
  return pathname;
}

function extractHotelOfferOrderId(value) {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) return s;
  const m = s.match(/^hotel-offer-(\d+)$/);
  return m ? m[1] : null;
}

function normalizeToCurrentOrigin(link, origin = "") {
  if (!link) return null;
  const o = origin || resolvePublicBaseUrl({ skipTrace: true });
  const raw = String(link).trim();
  if (!raw) return null;
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    try {
      const u = new URL(raw);
      const normalizedPath = normalizeDraftAppPath(u.pathname);
      const fullPath = `${normalizedPath}${u.search || ""}${u.hash || ""}`;
      return o ? `${o}${fullPath}` : fullPath;
    } catch {
      return raw;
    }
  }
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  const normalizedPath = normalizeDraftAppPath(path);
  return o ? `${o}${normalizedPath}` : normalizedPath;
}

function postDebugLog(runId, hypothesisId, location, message, data = {}) {
  void runId;
  void hypothesisId;
  void location;
  void message;
  void data;
}

/** מחירים מה-API לעיתים כמחרוזת עם פסיקים / ריקים — מניעת NaN בטבלה */
function parseNumericIls(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const s = String(raw)
    .replace(/[\s\u200f\u200e]/g, "")
    .replace(/,/g, "")
    .replace(/₪/g, "")
    .trim();
  if (s === "" || s === "-") return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * מחלץ מחיר מ-ourprice (יכול להיות מספר או אובייקט JSON).
 * אם זה אובייקט, מחפש priceWithCreditCardFee, finalPrice, או שדות מחיר אחרים.
 * אם זה מספר או string, מחזיר אותו אחרי parse.
 * אם אין מחיר תקף, מחזיר null.
 */
function extractPriceFromOurPrice(ourprice) {
  if (ourprice == null) return null;
  
  // אם זה מספר תקף, החזר אותו
  if (typeof ourprice === "number" && Number.isFinite(ourprice)) {
    return ourprice;
  }
  
  // אם זה אובייקט, נסה לחלץ מחיר מתוכו
  if (typeof ourprice === "object" && !Array.isArray(ourprice)) {
    // נסה שדות מחיר נפוצים לפי סדר עדיפות
    const priceFields = [
      "display_total_ils",
      "public_final_customer_price_ils",
      "final_customer_price",
      "priceWithCreditCardFee",
      "price_with_credit_card_fee",
      "finalPrice",
      "final_price",
      "totalPrice",
      "total_price",
      "price",
      "amount",
      "value",
    ];
    
    for (const field of priceFields) {
      const val = ourprice[field];
      if (val != null) {
        const parsed = parseNumericIls(val);
        if (parsed != null) return parsed;
      }
    }
    
    // אם לא מצאנו שדה ספציפי, נסה לפרסר את האובייקט עצמו (לא סביר אבל אפשרי)
    return null;
  }
  
  // אם זה string או משהו אחר, נסה לפרסר
  return parseNumericIls(ourprice);
}

function formatDestinationHe(raw) {
  if (raw == null || String(raw).trim() === "") return "—";
  const s = String(raw).trim();
  try {
    const t = translateLDSDestinations(s);
    if (t && typeof t === "object" && t.CITYNAME_HEB) return t.CITYNAME_HEB;
  } catch {
    /* ignore */
  }
  return s;
}

function normalizeTextArray(values) {
  if (!Array.isArray(values)) return [];
  return values
    .map((v) => (v == null ? "" : String(v).trim()))
    .filter(Boolean);
}

function tooltipFromList(values) {
  const arr = normalizeTextArray(values);
  return arr.length ? arr.join("\n") : undefined;
}

function getPackageCompareHotelLabel(link) {
  const hotelList = normalizeTextArray(link?.hotels_list);
  if (hotelList.length > 1) return `${hotelList[0]} ועוד ${hotelList.length - 1}`;
  if (hotelList.length === 1) return hotelList[0];
  const count = Number(link?.packages_count);
  if (Number.isFinite(count) && count > 1) return `השוואת ${count} חבילות`;
  return link?.hotel_name || "השוואת חבילות";
}

function getPackageCompareDestinationLabel(link) {
  return link?.destinations_summary || link?.destination || "—";
}

function getPackageCompareDatesLabel(link) {
  if (link?.dates_summary) return link.dates_summary;
  const dep = link?.dep_date || "";
  const ret = link?.ret_date || "";
  if (dep && ret) return `${dep} - ${ret}`;
  return dep || ret || "—";
}

function getOfferOwnerDisplayName(link) {
  if (!link) return "-";
  const name =
    link.agent_name ||
    `${(link.agent_firstname || "").trim()} ${(link.agent_lastname || "").trim()}`.trim();
  return name || "-";
}

/** Resolve draft/link URL from various possible API field names. Single place for SSOT. */
function getResolvedLink(row, origin = "") {
  const o = origin || resolvePublicBaseUrl({ skipTrace: true });
  let link =
    row.public_link ??
    row.customer_link_url ??
    row.link ??
    row.url ??
    row.href ??
    row.checkout_url ??
    row.site_url ??
    row.draft_url ??
    row.profile_url ??
    null;
  if (!link && row.token) {
    link = `/offers/public/${encodeURIComponent(String(row.token))}`;
  }
  if (!link && row.profile_uuid) link = `${o}/packages/profile/draft/${row.profile_uuid}`;
  if (!link && row.profileUuid) link = `${o}/packages/profile/draft/${row.profileUuid}`;
  if (!link && row.id) link = `${o}/packages/profile/draft/${row.id}`;
  if (!link) return null;
  return normalizeToCurrentOrigin(link, origin);
}

/** Normalize a single draft from API (camelCase or snake_case) to table row with resolved link. */
function normalizeDraftRow(d, origin) {
  const resolvedLink = getResolvedLink(d, origin);
  const agentName = [
    d.agentFirstName ?? d.agent_firstname,
    d.agentLastName ?? d.agent_lastname,
  ]
    .filter(Boolean)
    .join(" ")
    .trim() || null;
  return {
    id: d.id,
    deal_type: d.dealType ?? d.deal_type,
    product_type: d.dealType ?? d.deal_type,
    hotel_name: d.hotelName ?? d.hotel_name,
    destination: d.destination,
    dep_date: d.depDate ?? d.dep_date,
    ret_date: d.retDate ?? d.ret_date,
    total_price_ils: parseNumericIls(d.totalPrice ?? d.total_price),
    total_price: parseNumericIls(d.totalPrice ?? d.total_price),
    created_at: d.createdAt ?? d.created_at,
    customer_link_url: resolvedLink,
    url: resolvedLink,
    dealid: d.dealId ?? d.deal_id,
    handling_fee: d.handling_fee ?? null,
    agent_name: agentName,
    agent_firstname: d.agentFirstName ?? d.agent_firstname,
    agent_lastname: d.agentLastName ?? d.agent_lastname,
  };
}

/** Normalize LDS order (lds_orders) to table row shape. Link = /packages/profile/:id (order profile). */
const _DRAFTS_BOOKING_REF_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function getDraftOfferNumberLabel(link, sequentialFallback) {
  if (link?._orgTourResolvedHr) {
    return link._orgTourResolvedHr;
  }
  if (!link) {
    return sequentialFallback != null ? String(sequentialFallback) : "—";
  }
  if (link.draft_source === "hotel_offer") {
    return extractHotelOfferOrderId(link.gimmonix_order_id ?? link.id) || "—";
  }
  const raw = link.booking_ref ?? link.bookingRef;
  if (raw == null || String(raw).trim() === "") {
    return sequentialFallback != null ? String(sequentialFallback) : "—";
  }
  const s = String(raw).trim();
  if (_DRAFTS_BOOKING_REF_UUID_RE.test(s)) {
    return sequentialFallback != null ? String(sequentialFallback) : "—";
  }
  if (/^HR-/i.test(s)) return s;
  if (/^\d{8}$/.test(s)) return `HR-${s}`;
  if (/^\d+$/.test(s)) return `HR-${s}`;
  return s;
}

function normalizeLdsOrderRow(lo, origin) {
  const o = origin || resolvePublicBaseUrl({ skipTrace: true });
  const id = lo.id != null ? String(lo.id) : null;
  const dealType = lo.dealtype ?? lo.dealType ?? null;
  const offerType = lo.offer_type ?? lo.offerType ?? null;
  const draftSource = lo.draft_source ?? lo.draftSource;
  const isHotelOffer =
    draftSource === "hotel_offer" || (id != null && String(id).startsWith("hotel-offer-"));
  const isFlightDraft = dealType === "Flights" || dealType === "Flight";
  const isOrganizedTour = dealType === "OrganizedTour";
  const dynamicPackageId =
    lo.dynamic_package_id ??
    lo.dynamicPackageId ??
    lo.dynamicid ??
    lo.dynamicId ??
    null;
  const routeLabel =
    lo.route_label ??
    lo.routeLabel ??
    null;

  let profileUrl = null;
  if (isHotelOffer) {
    const gmxOrderId =
      lo.gimmonix_order_id ??
      lo.gimmonixOrderId ??
      extractHotelOfferOrderId(id);
    if (gmxOrderId != null && String(gmxOrderId).trim() !== "") {
      profileUrl = `${o}/hotel/offer/${encodeURIComponent(String(gmxOrderId))}`;
    }
  } else if (isOrganizedTour) {
    const pubTok = lo.public_access_token ?? lo.publicAccessToken;
    if (pubTok) {
      profileUrl = normalizeToCurrentOrigin(tokenToPublicOfferUrl(pubTok), o);
    } else {
      const rawShare =
        lo.public_share_url ??
        lo.publicShareUrl ??
        (dynamicPackageId != null
          ? buildPublicFlightOrDynamicOfferUrl(o, dynamicPackageId, { flightOnly: false })
          : null);
      profileUrl = rawShare ? normalizeToCurrentOrigin(rawShare, o) : null;
      if (!profileUrl && id) {
        profileUrl = normalizeToCurrentOrigin(organizedTourBookingPath(id), o);
      }
    }
  } else {
    const rawPublicShareUrl =
      lo.public_share_url ??
      lo.publicShareUrl ??
      (dynamicPackageId != null
        ? buildPublicFlightOrDynamicOfferUrl(o, dynamicPackageId, { flightOnly: !!isFlightDraft })
        : null);
    const publicShareUrl = normalizeToCurrentOrigin(rawPublicShareUrl, o);
    profileUrl = isFlightDraft
      ? publicShareUrl
      : id
        ? `${o}/packages/profile/${id}`
        : null;
    if (isFlightDraft) {
      // keep branch for parity with prior flow
    }
  }
  const agentName = [
    lo.agent_firstname ?? lo.agentFirstName,
    lo.agent_lastname ?? lo.agentLastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim() || null;
  const paid = !!lo.paid;
  const cancelled = !!lo.cancelled;
  let orderStatus = "בהליך";
  if (cancelled) orderStatus = "בוטל";
  else if (paid) orderStatus = "שולם";
  
  // חילוץ מחיר: ourprice יכול להיות מספר או אובייקט JSON
  const extractedPrice = extractPriceFromOurPrice(lo.ourprice ?? lo.ourPrice);
  const finalPriceIlsField = parseNumericIls(lo.final_price_ils ?? lo.finalPriceIls);
  const apiTotal = parseNumericIls(lo.total_price ?? lo.totalPrice);
  let finalPrice;
  let displayHotel;
  let normalizedDestination;
  let orgTourHr = null;

  if (isOrganizedTour) {
    const ot = buildOrganizedTourDraftRow(lo, { origin: o });
    profileUrl = ot.publicLink ?? profileUrl;
    normalizedDestination = ot.destination;
    displayHotel = ot.routeDisplay;
    finalPrice = ot.finalPriceDisplay;
    orgTourHr = ot.hrDisplay;
  } else {
    finalPrice =
      extractedPrice != null
        ? extractedPrice
        : finalPriceIlsField != null
          ? finalPriceIlsField
          : apiTotal;

    displayHotel = lo.hotel_name ?? lo.hotelName ?? (isFlightDraft ? routeLabel : null);
    normalizedDestination = lo.destination ?? null;
  }

  const roomName = lo.room_name ?? lo.roomName ?? null;
  const roomBasis = lo.room_basis ?? lo.roomBasis ?? null;
  const roomSummary =
    [roomName, roomBasis].filter((x) => x != null && String(x).trim() !== "").join(" · ") || null;

  if (!routeLabel || !normalizedDestination || finalPrice == null) {
    }

  return {
    id: id ?? lo.id,
    agent_id: lo.agentid ?? lo.agentId ?? null,
    offer_type: offerType,
    deal_type: dealType,
    product_type: dealType,
    hotel_name: displayHotel,
    destination: normalizedDestination,
    route_label: routeLabel,
    dep_date: lo.dep_date ?? lo.depDate ?? null,
    ret_date: lo.ret_date ?? lo.retDate ?? null,
    total_price_ils: finalPrice,
    total_price: finalPrice,
    created_at: lo.created_at ?? lo.createdAt ?? null,
    customer_link_url: profileUrl,
    url: profileUrl,
    dealid: lo.dealid ?? lo.dealId ?? null,
    handling_fee: lo.handlingfee ?? lo.handling_fee ?? null,
    token: lo.public_access_token ?? lo.publicAccessToken ?? lo.token ?? null,
    public_link: lo.public_link ?? lo.public_offer_link ?? null,
    public_access_token: lo.public_access_token ?? lo.publicAccessToken ?? null,
    booking_ref: lo.booking_ref ?? lo.bookingRef ?? null,
    status: lo.status ?? null,
    entity_id: lo.entity_id ?? lo.entityId ?? lo.package_id ?? lo.packageId ?? null,
    package_id: lo.package_id ?? lo.packageId ?? lo.packageID ?? null,
    resume_package_id: lo.resume_package_id ?? lo.resumePackageId ?? null,
    gimmonix_order_id: lo.gimmonix_order_id ?? lo.gimmonixOrderId ?? null,
    hotel_id: lo.hotel_id ?? lo.hotelId ?? null,
    packages_count: lo.packages_count ?? null,
    min_price_ils: lo.min_price_ils ?? null,
    max_price_ils: lo.max_price_ils ?? null,
    destinations_summary: lo.destinations_summary ?? null,
    destinations_list: lo.destinations_list ?? null,
    dates_summary: lo.dates_summary ?? null,
    dates_list: lo.dates_list ?? null,
    hotels_list: lo.hotels_list ?? null,
    agent_name: agentName,
    agent_firstname: lo.agent_firstname ?? lo.agentFirstName,
    agent_lastname: lo.agent_lastname ?? lo.agentLastName,
    paid,
    cancelled,
    order_status: orderStatus,
    draft_source: isHotelOffer ? "hotel_offer" : null,
    dynamic_package_id: dynamicPackageId != null ? String(dynamicPackageId) : null,
    internal_draft_url:
      isOrganizedTour && id
        ? `${o}/p/dashboard/organized-tours/booking/${encodeURIComponent(id)}`
        : lo.internal_draft_url ??
          lo.internalDraftUrl ??
          (dynamicPackageId != null
            ? `${o}/p/dashboard/packages/proposal/${encodeURIComponent(String(dynamicPackageId))}`
            : null),
    continue_booking_url:
      isOrganizedTour && id
        ? `${o}/p/dashboard/organized-tours/booking/${encodeURIComponent(id)}`
        : dynamicPackageId != null
          ? `${o}/p/dashboard/dynamic/profile/${encodeURIComponent(String(dynamicPackageId))}`
          : null,
    room_summary: roomSummary,
    _orgTourResolvedHr: isOrganizedTour ? orgTourHr : undefined,
  };
}

function ymdKeyFromCreatedAt(createdAt) {
  if (!createdAt) return null;
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function formatMonthTabLabel(key) {
  const parts = key.split("-");
  const y = Number(parts[0]);
  const mo = Number(parts[1]);
  if (!y || !mo) return key;
  try {
    return new Date(y, mo - 1, 1).toLocaleDateString("he-IL", { month: "long", year: "numeric" });
  } catch {
    return key;
  }
}

function draftStatusKey(link) {
  if (!link) return "in_progress";
  if (link.cancelled) return "cancelled";
  if (link.paid) return "paid";
  return "in_progress";
}

/** SSOT למסלול הפותח את אותה URL כמו בטבלה (ללא שינוי כלל עסקי). */
function resolveOfferFullUrl(link) {
  const pubBase = resolvePublicBaseUrl({ skipTrace: true });
  const first = getResolvedLink(link);
  if (first) return first;
  if (link.offer_type === "package_compare") {
    if (link.public_link) return normalizeToCurrentOrigin(link.public_link);
    if (link.token) return `${pubBase}/offers/public/${encodeURIComponent(String(link.token))}`;
  }
  if (link.deal_type === "OrganizedTour" || link.product_type === "OrganizedTour") {
    const tok = link.public_access_token ?? link.token;
    if (tok) return normalizeToCurrentOrigin(tokenToPublicOfferUrl(tok), pubBase);
  }
  const isDraft = link.deal_type === "HotelsOnly";
  const pathSuffix = isDraft ? `packages/profile/draft/${link.id}` : `packages/profile/${link.id}`;
  return `${pubBase}/${pathSuffix}`;
}

function getDraftOrderType(link) {
  const rawValues = [
    link?.deal_type,
    link?.dealType,
    link?.product_type,
    link?.productType,
    link?.offer_type,
    link?.offerType,
    link?.draft_source,
    link?.draftSource,
    link?.dynamic_package_id != null ? "dynamic_package_id" : null,
  ]
    .map((v) => (v == null ? "" : String(v).trim()))
    .filter(Boolean);
  const normalizedValues = rawValues.map((v) => v.toLowerCase());

  if (normalizedValues.some((v) => ["flight", "flights"].includes(v))) {
    return "flights";
  }
  if (
    normalizedValues.some((v) =>
      ["dynamic", "dynamicpackage", "dynamic_package", "dynamic_package_id"].includes(v)
    )
  ) {
    return "dynamic-packages";
  }
  if (normalizedValues.some((v) => ["organizedtour", "organized_tour"].includes(v))) {
    return "organized-tours";
  }
  if (
    normalizedValues.some((v) =>
      ["hotel", "hotelsonly", "hoteloffer", "hotel_offer"].includes(v)
    )
  ) {
    return "hotels";
  }
  if (normalizedValues.some((v) => ["flightshotel", "package", "package_compare"].includes(v))) {
    return "packages";
  }

  return null;
}

function getDraftOrderId(link) {
  const rawId =
    link?.id ??
    link?.order_id ??
    link?.orderId ??
    link?.orderid ??
    link?.dealid ??
    link?.dealId ??
    null;
  const orderType = getDraftOrderType(link);
  const id =
    orderType === "hotels"
      ? extractHotelOfferOrderId(rawId) || rawId
      : rawId;
  if (id == null) return null;
  const s = String(id).trim();
  return s ? s : null;
}

function getDraftEntityId(link) {
  const rawId =
    link?.entity_id ??
    link?.entityId ??
    link?.package_id ??
    link?.packageId ??
    link?.packageID ??
    link?.resume_package_id ??
    link?.resumePackageId ??
    link?.dynamic_package_id ??
    link?.dynamicPackageId ??
    link?.dynamicid ??
    link?.dynamicId ??
    link?.id ??
    null;
  if (rawId == null) return null;
  const s = String(rawId).trim();
  return s ? s : null;
}

function getDraftDynamicPackageId(link) {
  const rawId =
    link?.dynamic_package_id ??
    link?.dynamicPackageId ??
    link?.dynamicid ??
    link?.dynamicId ??
    null;
  if (rawId == null) return null;
  const s = String(rawId).trim();
  return s ? s : null;
}

function getDashboardBaseFromPathname(pathname = "") {
  const p = String(pathname || "");
  if (p === "/p/dashboard" || p.startsWith("/p/dashboard/")) {
    return "/p/dashboard";
  }
  const localized = p.match(/^\/([a-z]{2}(?:-[A-Z]{2})?)\/dashboard(?:\/|$)/);
  if (localized?.[1]) {
    return `/${localized[1]}/dashboard`;
  }
  return "/p/dashboard";
}

function isPaymentRouteUrl(url) {
  if (!url) return false;
  const s = String(url);
  return s.includes("/p/dashboard/payment/") || /(^|\/)payment\//.test(s);
}

function firstNonPaymentUrl(...urls) {
  for (const url of urls) {
    if (!url || isPaymentRouteUrl(url)) continue;
    return url;
  }
  return null;
}

function resolveDraftPassengerDataUrl(link, fullUrl, pathname = "") {
  const orderId = getDraftOrderId(link);
  const orderType = getDraftOrderType(link);
  const dashboardBase = getDashboardBaseFromPathname(pathname);
  const entityId = getDraftEntityId(link);
  const dynamicPackageId = getDraftDynamicPackageId(link);
  const hotelOfferOrderId =
    link?.gimmonix_order_id ??
    link?.gimmonixOrderId ??
    extractHotelOfferOrderId(link?.id);
  const encodedOrderId = orderId ? encodeURIComponent(orderId) : null;
  const encodedEntityId = entityId ? encodeURIComponent(entityId) : null;
  const encodedDynamicId =
    dynamicPackageId ? encodeURIComponent(dynamicPackageId) : null;
  const encodedHotelOfferOrderId =
    hotelOfferOrderId != null && String(hotelOfferOrderId).trim() !== ""
      ? encodeURIComponent(String(hotelOfferOrderId).trim())
      : null;

  const isHebrewDashboard = dashboardBase === "/p/dashboard";
  const hotelOfferPath =
    encodedHotelOfferOrderId && encodedEntityId
      ? isHebrewDashboard
        ? `${dashboardBase}/searchoffer/${encodedEntityId}/hotel/${encodedHotelOfferOrderId}`
        : `${dashboardBase}/hotels/offer/${encodedHotelOfferOrderId}`
      : null;

  let passengerDataUrl = hotelOfferPath;
  if (!passengerDataUrl && orderType === "dynamic-packages" && (encodedDynamicId || encodedOrderId)) {
    passengerDataUrl = `${dashboardBase}/searchoffer/${encodedEntityId || encodedDynamicId || encodedOrderId}/dynamic/${encodedDynamicId || encodedOrderId}`;
  }
  if (!passengerDataUrl && orderType === "organized-tours" && encodedOrderId) {
    passengerDataUrl = `${dashboardBase}/organized-tours/booking/${encodedOrderId}`;
  }
  if (!passengerDataUrl && (encodedEntityId || encodedOrderId)) {
    passengerDataUrl = isHebrewDashboard
      ? `${dashboardBase}/searchoffer/${encodedEntityId || encodedOrderId}`
      : `${dashboardBase}/hotels/offer/${encodedEntityId || encodedOrderId}`;
  }

  return firstNonPaymentUrl(
    passengerDataUrl,
    fullUrl
  );
}

function getDraftRemainingBalance(link) {
  return (
    link?.remainingBalance ??
    link?.remaining_balance ??
    link?.admin_remaining_balance ??
    link?.payment_link_remaining_balance ??
    link?.payment_integrity?.remaining_balance ??
    link?.payment_integrity?.remainingBalance ??
    null
  );
}

function traceDraftListOpenLink(link, { fullUrl, targetDetailsUrl, offerNumber, reason }) {
  if (process.env.NODE_ENV !== "development") return;
  const linkType =
    link?.type ??
    link?.deal_type ??
    link?.dealType ??
    link?.product_type ??
    link?.productType ??
    link?.offer_type ??
    link?.offerType ??
    null;
  console.log("[CUSTOMER_LINK_OPEN_TARGET_TRACE]", {
    "link.id": link?.id ?? null,
    "link.type": linkType,
    entity_id: getDraftEntityId(link),
    oldUrl: fullUrl,
    newUrl: targetDetailsUrl,
    reason,
  });
  console.log("[ORDER_DRAFT_LIST_OPEN_LINK_TRACE]", {
    orderId: getDraftOrderId(link),
    offerNumber,
    orderType: getDraftOrderType(link),
    currentGeneratedUrl: fullUrl,
    targetDetailsUrl,
    paid: link?.paid ?? null,
    remainingBalance: getDraftRemainingBalance(link),
  });
}

function computeIsFlightRow(link) {
  return (
    link.deal_type === "Flight" ||
    link.deal_type === "Flights" ||
    link.product_type === "Flight" ||
    link.product_type === "Flights"
  );
}

function formatHandlingFeeInline(link) {
  const fee = parseNumericIls(link.handling_fee);
  if (fee != null && fee !== 0) {
    return `₪${fee.toLocaleString("he-IL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  return "—";
}

/** שימוש משותף בטבלת דסקטופ ובכרטיסי מובייל — אותה לוגיקת מחיר. */
function LinkRowPriceCell({ link, fxRate }) {
  if (link.offer_type === "package_compare") {
    const minPrice = parseNumericIls(link.min_price_ils);
    const maxPrice = parseNumericIls(link.max_price_ils);
    if (minPrice != null && maxPrice != null) {
      const minRounded = Math.round(minPrice);
      const maxRounded = Math.round(maxPrice);
      return (
        <div className="price-block">
          <span className="price-main">
            {minRounded === maxRounded
              ? `₪${minRounded.toLocaleString("he-IL")}`
              : `₪${minRounded.toLocaleString("he-IL")}–₪${maxRounded.toLocaleString("he-IL")}`}
          </span>
        </div>
      );
    }
  }
  const totalIls = parseNumericIls(link.total_price_ils ?? link.total_price);
  const hasIls = totalIls != null;
  const totalUsdDisplay = hasIls && fxRate ? totalUsdFromIls(totalIls, fxRate) : null;
  if (
    process.env.NODE_ENV === "development" &&
    hasIls &&
    (link.total_price_usd != null || link.usd_display != null)
  ) {
    const serverUsd = Number(link.total_price_usd ?? link.usd_display);
    if (
      Number.isFinite(serverUsd) &&
      totalUsdDisplay != null &&
      Math.abs(serverUsd - totalUsdDisplay) > 0.01
    ) {
      console.warn("[CustomerLinksTable] Price SSOT mismatch", {
        offerId: link.id,
        totalIls,
        fxRateUsed: fxRate,
        serverUsd,
        totalUsdFromSSOT: totalUsdDisplay,
      });
    }
  }
  if (hasIls) {
    return (
      <div className="price-block">
        <span className="price-main">
          ₪{totalIls.toLocaleString("he-IL", { maximumFractionDigits: 0 })}
        </span>
        {totalUsdDisplay != null && totalUsdDisplay > 0 && (
          <span className="price-usd">≈${Number(totalUsdDisplay).toFixed(2)}</span>
        )}
      </div>
    );
  }
  const fallback = parseNumericIls(link.total_price ?? link.price_with_credit_card_fee ?? link.price);
  const cur = link.currency || "ILS";
  if (fallback != null) {
    return (
      <div className="price-block">
        <span className="price-main">
          {cur === "ILS"
            ? `₪${fallback.toLocaleString("he-IL", { maximumFractionDigits: 0 })}`
            : `${fallback.toLocaleString("he-IL", { maximumFractionDigits: 2 })} ${cur}`}
        </span>
      </div>
    );
  }
  return (
    <div className="price-block">
      <span className="price-main placeholder-empty">—</span>
    </div>
  );
}

const CustomerLinksTable = ({ pageType = "sales-links" }) => {
  const { currencyRate } = useAppContext();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [backgroundRefreshing, setBackgroundRefreshing] = useState(false);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const user = getUserDetails();
  const isAdmin = isAdminUser();
  const fxRate = currencyRate?.USD;
  const isDraftsPage = pageType === "drafts";
  const pageTitle = isDraftsPage ? "טיוטות והצעות בהליך" : "קישורי מכירה";
  const statusFilter = React.useMemo(() => {
    const q = new URLSearchParams(location.search || "");
    return (q.get("status") || "").trim().toLowerCase();
  }, [location.search]);

  const monthParam = searchParams.get("month");
  const selectedMonthKey =
    monthParam && /^\d{4}-\d{2}$/.test(monthParam) ? monthParam : currentMonthKey();

  const monthKeysInData = React.useMemo(() => {
    const s = new Set();
    (links || []).forEach((l) => {
      const k = ymdKeyFromCreatedAt(l.created_at);
      if (k) s.add(k);
    });
    return Array.from(s).sort();
  }, [links]);

  const monthTabs = React.useMemo(() => {
    const keys = [...monthKeysInData];
    if (!keys.includes(selectedMonthKey)) {
      keys.push(selectedMonthKey);
    }
    return keys.sort();
  }, [monthKeysInData, selectedMonthKey]);

  const adminAgentFilter = (searchParams.get("agent_id") || "").trim();

  const agentOptions = React.useMemo(() => {
    const m = new Map();
    (links || []).forEach((l) => {
      const id = l.agent_id;
      if (id == null || id === "") return;
      const sid = String(id);
      if (m.has(sid)) return;
      const name = getOfferOwnerDisplayName(l);
      m.set(sid, name && name !== "-" ? name : `סוכן #${sid}`);
    });
    return Array.from(m.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "he"));
  }, [links]);

  const setAdminAgentFilter = React.useCallback(
    (agentId) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (agentId) p.set("agent_id", String(agentId));
          else p.delete("agent_id");
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const openCountInSelectedMonth = React.useMemo(() => {
    if (!isDraftsPage) return 0;
    return (links || []).filter((l) => {
      if (isAdmin && adminAgentFilter && String(l.agent_id) !== adminAgentFilter) return false;
      const k = ymdKeyFromCreatedAt(l.created_at);
      if (k !== selectedMonthKey) return false;
      return !l.cancelled && !l.paid;
    }).length;
  }, [links, isDraftsPage, selectedMonthKey, isAdmin, adminAgentFilter]);

  const filteredByMonth = React.useMemo(() => {
    if (!isDraftsPage) return links;
    return (links || []).filter((l) => ymdKeyFromCreatedAt(l.created_at) === selectedMonthKey);
  }, [links, isDraftsPage, selectedMonthKey]);

  const displayLinks = React.useMemo(() => {
    let rows = filteredByMonth;
    if (isDraftsPage && isAdmin && adminAgentFilter) {
      rows = (rows || []).filter((l) => String(l.agent_id) === adminAgentFilter);
    }
    if (!isDraftsPage) return rows;
    if (statusFilter !== "open") return rows;
    return (rows || []).filter(
      (l) =>
        l.order_status === "בהליך" ||
        (l.paid !== true && l.cancelled !== true)
    );
  }, [filteredByMonth, isDraftsPage, statusFilter, isAdmin, adminAgentFilter]);

  // חישוב מספר הצעה לפי dealid - מספר סידורי של ההצעה בתוך אותו deal
  const offerNumbersByDealId = React.useMemo(() => {
    if (!isDraftsPage || !Array.isArray(displayLinks)) return new Map();
    
    // קיבוץ לפי dealid
    const byDealId = new Map();
    displayLinks.forEach((link) => {
      const dealId = link.dealid;
      if (dealId == null || dealId === "") return;
      const key = String(dealId);
      if (!byDealId.has(key)) {
        byDealId.set(key, []);
      }
      byDealId.get(key).push(link);
    });
    
    // מיון כל קבוצה לפי תאריך יצירה (הכי ישן ראשון) ואז לפי id
    byDealId.forEach((links) => {
      links.sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        if (dateA !== dateB) return dateA - dateB;
        // אם אותו תאריך, מיון לפי id
        const idA = String(a.id || "").localeCompare(String(b.id || ""));
        return idA;
      });
    });
    
    // יצירת מפה של (link.id -> offerNumber)
    const result = new Map();
    byDealId.forEach((links) => {
      links.forEach((link, index) => {
        result.set(String(link.id), index + 1);
      });
    });
    
    return result;
  }, [displayLinks, isDraftsPage]);

  const setMonthInUrl = React.useCallback(
    (key) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set("month", key);
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const showPagination =
    Array.isArray(links) &&
    links.length > 0 &&
    !(isDraftsPage && statusFilter === "open");

  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false
  );
  const layoutMode = isMobile ? "cards" : "table";

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const traceTotalPages = showPagination ? MAX_PAGES : 1;

  React.useEffect(() => {
    if (!import.meta.env.DEV || typeof window === "undefined") return;
    console.log("[PROPOSALS_MOBILE_LAYOUT_TRACE]", {
      viewportWidth: window.innerWidth,
      isMobile,
      layoutMode,
      rowsCount: Array.isArray(displayLinks) ? displayLinks.length : 0,
      currentPage,
      totalPages: traceTotalPages,
    });
  }, [isMobile, layoutMode, displayLinks, currentPage, traceTotalPages]);

  const SKELETON_NEW_ROWS = 3;
  const SKELETON_INITIAL_ROWS = 8;
  const AUTO_REFRESH_MS = 120 * 1000; // 2 דקות – רענון ברקע בלי להעמיס
  const hasLinksRef = React.useRef(false);

  React.useEffect(() => {
    hasLinksRef.current = Array.isArray(links) && links.length > 0;
  }, [links]);

  useEffect(() => {
    fetchLinks();
  }, [currentPage]);

  // רענון אוטומטי כל דקה: מוסיף רק תוצאות חדשות (בלי להחליף את הקיימות); במהלך הרענון מוצג סקלטון לשורות החדשות בלבד.
  useEffect(() => {
    if (loading || error) return;
    const interval = setInterval(() => {
      fetchLinks(1, { silent: true }); // תמיד עמוד 1 כדי לזהות רשומות חדשות
    }, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [loading, error]);

  const fetchLinks = async (pageOverride = null, options = {}) => {
    const silent = options.silent === true;
    try {
      const page = pageOverride != null ? pageOverride : currentPage;
      if (!silent) {
        setError(null);
        if (!hasLinksRef.current) setLoading(true);
      } else {
        setBackgroundRefreshing(true);
      }
      const sessionID = await getSessionId();
      const offset = (page - 1) * PAGE_SIZE;
      const requestData = {
        agent_id: isAdmin ? null : user?.id,
        sessionID: sessionID,
        limit: PAGE_SIZE,
        offset: offset
      };

      if (process.env.NODE_ENV === "development" && isDraftsPage) {
        console.log("[CustomerLinksTable:drafts] current user:", user?.email ?? user?.id, "role:", user?.role, "isAdmin:", isAdmin);
      }

      const draftsOpts = {
        sessionID,
        agent_id: isAdmin ? null : user?.id,
        limit: PAGE_SIZE,
        offset,
        include_cancelled: true,
        include_order_response: true,
      };
      if (silent) {
        draftsOpts.include_hotel_offer_drafts = false;
        draftsOpts.include_package_compare = false;
      }
      const response = isDraftsPage
        ? await getLdsDrafts(draftsOpts)
        : await getCustomerLinks(requestData);

      if (!response) {
        if (!silent) {
          setError("הבקאנד לא זמין כרגע. אנא נסה שוב מאוחר יותר.");
          setLinks([]);
        }
        return;
      }

      if (response?.status && (response.status >= 400)) {
        const errMsg = response?.data?.error?.message || response?.data?.message || "שגיאה בטעינת הלינקים";
        if (!silent) {
          if (response.status === 401) setError("נדרש אימות. אנא התחבר מחדש.");
          else setError(errMsg);
          setLinks((prev) => (prev.length ? prev : []));
        }
        return;
      }

      if (response?.data?.success === false) {
        const errMsg = response?.data?.error?.message || response?.data?.message || "שגיאה בטעינת הלינקים";
        if (!silent) {
          if (response?.data?.error?.code === "UNAUTHORIZED") setError("נדרש אימות. אנא התחבר מחדש.");
          else setError(errMsg);
          setLinks((prev) => (prev.length ? prev : []));
        }
        return;
      }

      const origin = resolvePublicBaseUrl({ skipTrace: true });
      let linksData = null;
      if (isDraftsPage) {
        const rawDrafts =
          response?.data?.drafts ??
          response?.data?.data ??
          response?.data?.items ??
          [];
        linksData = Array.isArray(rawDrafts)
          ? rawDrafts.map((d) => normalizeLdsOrderRow(d, origin))
          : [];
        if (process.env.NODE_ENV === "development") {
          console.log("[CUSTOMER_LINKS_PACKAGE_COMPARE_DEBUG]", {
            endpoint: "/api/lds-drafts",
            total: linksData.length,
            packageCompareRows: linksData.filter((r) => r.offer_type === "package_compare"),
            sample: linksData.slice(0, 5),
          });
        }
      } else if (response?.data?.data) {
        linksData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        linksData = response.data;
      }

      if (!isDraftsPage && Array.isArray(linksData)) {
        const rows = linksData;
        console.log("[CUSTOMER_LINKS_PACKAGE_COMPARE_DEBUG]", {
          endpoint: "/api/getCustomerLinks",
          total: rows.length,
          packageCompareRows: rows.filter((r) => r.offer_type === "package_compare"),
          sample: rows.slice(0, 5),
        });
      }

      if (Array.isArray(linksData)) {
        if (silent) {
          setLinks((prev) => {
            const existingIds = new Set((prev || []).map((l) => String(l.id)));
            const newItems = linksData.filter((l) => !existingIds.has(String(l.id)));
            if (newItems.length === 0) return prev;
            return [...newItems, ...prev];
          });
        } else {
          setLinks(linksData);
          if (pageOverride != null) setCurrentPage(pageOverride);
        }
      } else if (!silent) {
        setLinks([]);
      }
    } catch (err) {
      if (!silent) {
        if (err?.message?.includes("CORS") || err?.message?.includes("Failed to fetch")) setError("הבקאנד לא זמין כרגע. אנא נסה שוב מאוחר יותר.");
        else if (err?.response?.status === 401) setError("נדרש אימות. אנא התחבר מחדש.");
        else setError("שגיאה בטעינת הלינקים");
        setLinks((prev) => (prev.length ? prev : []));
      }
    } finally {
      if (!silent) {
        setLoading(false);
        setRefreshing(false);
      }
      setBackgroundRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    if (currentPage === 1) {
      fetchLinks(1);
    } else {
      setCurrentPage(1);
    }
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > MAX_PAGES) return;
    setCurrentPage(page);
  };

  const handleCopyLink = async (linkId, fullUrl) => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      showSuccessToast("הקישור הועתק בהצלחה!");
    } catch (err) {
      errorMessage("שגיאה בהעתקת הקישור");
    }
  };

  const handleOpenLink = (urlToOpen) => {
    if (!urlToOpen) return;
    // קישור ציבורי/לקוח — תמיד דומיין קנוני מה-ENV, לא host של preview
    let path = urlToOpen;
    if (urlToOpen.startsWith("http://") || urlToOpen.startsWith("https://")) {
      try {
        const u = new URL(urlToOpen);
        path = u.pathname + (u.search || "") + (u.hash || "");
      } catch {
        path = urlToOpen;
      }
    }
    const pubBase = resolvePublicBaseUrl({ skipTrace: true });
    const href = path.startsWith("/") ? `${pubBase}${path}` : `${pubBase}/${path}`;
    window.open(href, "_blank");
  };

  const handleDraftStatusChange = async (link, newStatus) => {
    if (!isDraftsPage || !link?.id) return;
    if (draftStatusKey(link) === newStatus) return;
    setStatusUpdatingId(link.id);
    try {
      const sessionID = await getSessionId();
      if (!sessionID) {
        errorMessage("נדרש אימות");
        return;
      }
      const res = await updateLdsDraftStatus(link.id, sessionID, newStatus);
      if (res?.data?.success === true) {
        const paid = !!res.data.paid;
        const cancelled = !!res.data.cancelled;
        const orderStatus = cancelled ? "בוטל" : paid ? "שולם" : "בהליך";
        setLinks((prev) =>
          (prev || []).map((l) =>
            String(l.id) === String(link.id) ? { ...l, paid, cancelled, order_status: orderStatus } : l
          )
        );
        showSuccessToast("הסטטוס עודכן");
        window.dispatchEvent(new Event("ldsDraftsChanged"));
      } else {
        const msg = res?.data?.error?.message || "עדכון הסטטוס נכשל";
        errorMessage(msg);
      }
    } catch {
      errorMessage("שגיאה בעדכון הסטטוס");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleDeleteOffer = async (link) => {
    if (!window.confirm("למחוק את ההצעה מהטבלה? פעולה זו תמחק את ההצעה לצמיתות.")) return;
    try {
      const sessionID = await getSessionId();
      const response = isDraftsPage
        ? await deleteLdsDraft(link.id, sessionID)
        : await deleteCustomerLink({ sessionID, order_id: link.id });
      if (response?.data?.success === true) {
        showSuccessToast(isDraftsPage ? "הטיוטה נמחקה בהצלחה" : "ההצעה נמחקה בהצלחה");
        if (isDraftsPage) {
          window.dispatchEvent(new Event("ldsDraftsChanged"));
        }
        fetchLinks();
      } else {
        const msg = response?.data?.error?.message || "מחיקה נכשלה";
        errorMessage(msg);
      }
    } catch (err) {
      errorMessage("שגיאה במחיקת ההצעה");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("he-IL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getProductTypeLabel = (dealType) => {
    if (!dealType) return "לא מוגדר";
    
    const typeMap = {
      "FlightsHotel": "חבילה",
      "Hotel": "מלון",
      "HotelsOnly": "מלונות",
      "HotelOffer": "מלון",
      "Dynamic": "חבילה דינמית",
      "OrganizedTour": "טיול מאורגן",
      "Flight": "טיסה",
      "Flights": "טיסה",
      "flight": "טיסה",
      "package_compare": "השוואת חבילות"
    };
    
    return typeMap[dealType] || dealType;
  };

  const getProductTypeClass = (dealType) => {
    if (!dealType) return "unknown";

    const classMap = {
      "FlightsHotel": "package",
      "Hotel": "hotel",
      "HotelsOnly": "hotel",
      "HotelOffer": "hotel",
      "Dynamic": "dynamic",
      "OrganizedTour": "tour",
      "Flight": "flight",
      "Flights": "flight",
      "flight": "flight",
      "package_compare": "package-compare"
    };

    return classMap[dealType] || "unknown";
  };

  if (error && !Array.isArray(links)?.length) {
    return (
      <div className="customer-links-table-container">
        <header className="customer-links-card-header">
          <h1 className="customer-links-title">{pageTitle}</h1>
          {isAdmin && <span className="admin-badge">מצב אדמין</span>}
        </header>
        <div className="error-state">
          <p className="error-state-message">{error}</p>
          <p className="error-state-hint">אנא נסה לרענן את הדף או לבדוק את החיבור לשרת.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`customer-links-table-container${isMobile ? " customer-links-table-container--mobile" : ""}`}>
      <header className={`customer-links-card-header${isMobile ? " customer-links-card-header--mobile" : ""}`}>
        <div className="customer-links-card-heading">
          <h1 className="customer-links-title">{pageTitle}</h1>
          {isDraftsPage && (
            <p className="customer-links-subtitle">ניהול הצעות וחבילות שנשלחו ללקוחות</p>
          )}
          {isDraftsPage && statusFilter === "open" && (
            <p className="customer-links-filter-hint" dir="rtl">
              מציג: <strong>הצעות פתוחות</strong> בלבד (בהליך)
            </p>
          )}
          {isDraftsPage && (
            <p className="customer-links-open-in-month" dir="rtl">
              <strong>{openCountInSelectedMonth}</strong> הצעות פתוחות (בהליך) בחודש{" "}
              {formatMonthTabLabel(selectedMonthKey)}
            </p>
          )}
          {isDraftsPage && isAdmin && agentOptions.length > 0 && (
            <div className="drafts-admin-agent-filter" dir="rtl">
              <label htmlFor="drafts-agent-filter" className="drafts-admin-agent-filter__label">
                סינון לפי סוכן
              </label>
              <select
                id="drafts-agent-filter"
                className="drafts-admin-agent-filter__select"
                value={adminAgentFilter}
                onChange={(e) => setAdminAgentFilter(e.target.value)}
              >
                <option value="">כל הסוכנים</option>
                {agentOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name} (#{o.id})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="customer-links-meta">
            {loading && (
              <span className="loading-badge" aria-live="polite">טוען...</span>
            )}
            {!loading && Array.isArray(displayLinks) && displayLinks.length > 0 && (
              <span className="offers-count-badge">
                {isDraftsPage
                  ? `${displayLinks.length} מוצגות בחודש (${formatMonthTabLabel(selectedMonthKey)})`
                  : isAdmin
                    ? `סה״כ ${displayLinks.length} הצעות`
                    : `${displayLinks.length} הצעות`}
              </span>
            )}
            {isAdmin && (
              <span className="admin-badge">מצב אדמין</span>
            )}
          </div>
        </div>
        <div className="customer-links-card-actions">
          <button
            type="button"
            className="refresh-btn-icon"
            onClick={handleRefresh}
            disabled={loading || refreshing}
            title="רענון רשימה"
            aria-label="רענון רשימה"
          >
            <FaSyncAlt className={refreshing ? "spin" : ""} />
          </button>
        </div>
      </header>

      {isDraftsPage && monthTabs.length > 0 && (
        <div className="drafts-month-tabs" dir="rtl" role="tablist" aria-label="סינון לפי חודש">
          {monthTabs.map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={key === selectedMonthKey}
              className={`drafts-month-tab ${key === selectedMonthKey ? "is-active" : ""}`}
              onClick={() => setMonthInUrl(key)}
            >
              {formatMonthTabLabel(key)}
            </button>
          ))}
        </div>
      )}

      {showPagination && (
        <>
          {isMobile ? (
            <div className="customer-links-pagination customer-links-pagination--compact">
              <button
                type="button"
                className="pagination-btn pagination-btn--nav"
                disabled={currentPage <= 1 || loading}
                onClick={() => handlePageChange(currentPage - 1)}
                aria-label="עמוד קודם"
              >
                הקודם
              </button>
              <span className="pagination-info pagination-info--compact" dir="rtl">
                עמוד {currentPage} מתוך {MAX_PAGES}
              </span>
              <button
                type="button"
                className="pagination-btn pagination-btn--nav"
                disabled={currentPage >= MAX_PAGES || loading}
                onClick={() => handlePageChange(currentPage + 1)}
                aria-label="עמוד הבא"
              >
                הבא
              </button>
            </div>
          ) : (
            <div className="customer-links-pagination">
              <span className="pagination-info">
                עמוד {currentPage} מתוך {MAX_PAGES}
              </span>
              <div className="pagination-buttons">
                <button
                  type="button"
                  className="pagination-btn"
                  disabled={currentPage <= 1 || loading}
                  onClick={() => handlePageChange(currentPage - 1)}
                  aria-label="עמוד קודם"
                >
                  ← הקודם
                </button>
                {Array.from({ length: MAX_PAGES }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`pagination-btn ${p === currentPage ? "active" : ""}`}
                    disabled={loading}
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  className="pagination-btn"
                  disabled={currentPage >= MAX_PAGES || loading}
                  onClick={() => handlePageChange(currentPage + 1)}
                  aria-label="עמוד הבא"
                >
                  הבא →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {isMobile ? (
        <div className="customer-links-mobile-root" dir="rtl">
          {(backgroundRefreshing || (refreshing && Array.isArray(links) && links.length > 0)) &&
            !loading && (
              <p className="customer-links-mobile-sync-hint" aria-live="polite">
                מעדכן נתונים…
              </p>
            )}
          {(backgroundRefreshing || (refreshing && Array.isArray(links) && links.length > 0)) &&
            Array.from({ length: 3 }, (_, i) => (
              <div key={`mob-sk-live-${i}`} className="customer-links-mobile-skeleton-card" aria-hidden="true">
                <div className="customer-links-mobile-skeleton-card__hdr">
                  <div className="customer-links-mobile-skeleton-line customer-links-mobile-skeleton-line--md" />
                  <div className="customer-links-mobile-skeleton-line customer-links-mobile-skeleton-line--sm" />
                </div>
                <div className="customer-links-mobile-skeleton-card__body">
                  <div className="customer-links-mobile-skeleton-line customer-links-mobile-skeleton-line--lg" />
                  <div className="customer-links-mobile-skeleton-line" />
                </div>
                <div className="customer-links-mobile-skeleton-actions">
                  <div className="customer-links-mobile-skeleton-pill" />
                </div>
              </div>
            ))}
          {loading && !(Array.isArray(links) && links.length > 0)
            ? Array.from({ length: 7 }, (_, i) => (
                <div key={`mob-sk-${i}`} className="customer-links-mobile-skeleton-card" aria-hidden="true">
                  <div className="customer-links-mobile-skeleton-card__hdr">
                    <div className="customer-links-mobile-skeleton-line customer-links-mobile-skeleton-line--md" />
                    <div className="customer-links-mobile-skeleton-line customer-links-mobile-skeleton-line--sm" />
                  </div>
                  <div className="customer-links-mobile-skeleton-card__body">
                    <div className="customer-links-mobile-skeleton-line customer-links-mobile-skeleton-line--lg" />
                    <div className="customer-links-mobile-skeleton-line" />
                    <div className="customer-links-mobile-skeleton-line" />
                  </div>
                  <div className="customer-links-mobile-skeleton-actions">
                    <div className="customer-links-mobile-skeleton-pill" />
                    <div className="customer-links-mobile-skeleton-pill customer-links-mobile-skeleton-pill--narrow" />
                  </div>
                </div>
              ))
            : null}
          {!loading &&
          !backgroundRefreshing &&
          (!Array.isArray(displayLinks) || displayLinks.length === 0) ? (
            <div className="customer-links-mobile-empty">
              <div className="empty-state">
                <p>
                  {isDraftsPage && statusFilter === "open" && Array.isArray(filteredByMonth) && filteredByMonth.length > 0
                    ? "אין הצעות פתוחות בחודש זה"
                    : isDraftsPage && Array.isArray(links) && links.length > 0
                      ? "אין הצעות בחודש שנבחר"
                      : "אין קישורים זמינים"}
                </p>
              </div>
            </div>
          ) : null}
          {Array.isArray(displayLinks) && displayLinks.length > 0
            ? displayLinks.map((link) => {
                const fullUrl = resolveOfferFullUrl(link);
                const offerNumLabel = isDraftsPage
                  ? getDraftOfferNumberLabel(link, offerNumbersByDealId.get(String(link.id)))
                  : null;
                const targetDetailsUrl = isDraftsPage
                  ? resolveDraftPassengerDataUrl(link, fullUrl, location.pathname) || fullUrl
                  : fullUrl;
                if (isDraftsPage) {
                  traceDraftListOpenLink(link, {
                    fullUrl,
                    targetDetailsUrl,
                    offerNumber: offerNumLabel,
                    reason: "draft_open_passenger_data_flow",
                  });
                }
                const isFlightRow = computeIsFlightRow(link);
                const draftOpenUrl = isDraftsPage
                  ? targetDetailsUrl
                  : link.internal_draft_url || fullUrl;
                const continueBookingUrl = isDraftsPage
                  ? targetDetailsUrl
                  : link.continue_booking_url || draftOpenUrl;
                const hotelRouteLabel =
                  link.offer_type === "package_compare"
                    ? getPackageCompareHotelLabel(link)
                    : link.hotel_name || link.route_label || "—";
                const destinationLabel =
                  link.offer_type === "package_compare"
                    ? getPackageCompareDestinationLabel(link)
                    : formatDestinationHe(link.destination);
                const datesLine =
                  link.offer_type === "package_compare"
                    ? getPackageCompareDatesLabel(link)
                    : [link.dep_date || "—", link.ret_date || "—"].filter(Boolean).join(" · ");
                const primaryOpenTarget = targetDetailsUrl || draftOpenUrl;
                const primaryOpenLabel = isDraftsPage ? "פתח טיוטה לעדכון" : "פתח הצעה";

                return (
                  <article key={link.id} className="customer-links-mobile-card">
                    <div className="customer-links-mobile-card__header">
                      <span
                        className={`product-type-badge product-type-${getProductTypeClass(
                          link.offer_type === "package_compare" ? "package_compare" : link.deal_type || link.product_type
                        )}`}
                      >
                        {getProductTypeLabel(
                          link.offer_type === "package_compare" ? "package_compare" : link.deal_type || link.product_type
                        )}
                      </span>
                      {isDraftsPage ? (
                        <span className="customer-links-mobile-card__offer-id" dir="rtl">
                          הצעה #{offerNumLabel}
                        </span>
                      ) : null}
                    </div>
                    {isDraftsPage ? (
                      <div className="customer-links-mobile-card__status">
                        <select
                          className="draft-status-select draft-status-select--mobile-card"
                          value={draftStatusKey(link)}
                          disabled={statusUpdatingId === link.id}
                          onChange={(e) => handleDraftStatusChange(link, e.target.value)}
                          aria-label="שינוי סטטוס הצעה"
                        >
                          <option value="in_progress">בהליך</option>
                          <option value="paid">שולם</option>
                          <option value="cancelled">בוטל</option>
                        </select>
                      </div>
                    ) : null}
                    <div className="customer-links-mobile-card__body">
                      <dl className="customer-links-mobile-dl">
                        <div className="customer-links-mobile-dl__row">
                          <dt>סוכן</dt>
                          <dd>{getOfferOwnerDisplayName(link)}</dd>
                        </div>
                        <div className="customer-links-mobile-dl__row">
                          <dt>מלון / מסלול</dt>
                          <dd>{hotelRouteLabel}</dd>
                        </div>
                        <div className="customer-links-mobile-dl__row">
                          <dt>יעד</dt>
                          <dd>{destinationLabel}</dd>
                        </div>
                        <div className="customer-links-mobile-dl__row">
                          <dt>תאריכים</dt>
                          <dd>{datesLine}</dd>
                        </div>
                        <div className="customer-links-mobile-dl__row">
                          <dt>תאריך יצירה</dt>
                          <dd className="cell-muted">{formatDate(link.created_at)}</dd>
                        </div>
                        <div className="customer-links-mobile-dl__row customer-links-mobile-dl__row--price">
                          <dt>מחיר</dt>
                          <dd>
                            <LinkRowPriceCell link={link} fxRate={fxRate} />
                          </dd>
                        </div>
                      </dl>
                    </div>
                    <div className="customer-links-mobile-card__actions">
                      {primaryOpenTarget ? (
                        <button
                          type="button"
                          className="customer-links-mobile-cta-primary"
                          onClick={() => handleOpenLink(primaryOpenTarget)}
                        >
                          {primaryOpenLabel}
                        </button>
                      ) : null}
                      {isDraftsPage && isFlightRow && draftOpenUrl ? (
                        <button
                          type="button"
                          className="customer-links-mobile-btn-secondary"
                          onClick={() => handleOpenLink(targetDetailsUrl)}
                        >
                          פתח טיוטה
                        </button>
                      ) : null}
                      {isDraftsPage && isFlightRow && continueBookingUrl ? (
                        <button
                          type="button"
                          className="customer-links-mobile-btn-secondary customer-links-mobile-btn-secondary--emphasis"
                          onClick={() => handleOpenLink(targetDetailsUrl)}
                        >
                          המשך להזמנה
                        </button>
                      ) : null}
                      {targetDetailsUrl ? (
                        <button
                          type="button"
                          className="customer-links-mobile-btn-ghost"
                          onClick={() => handleCopyLink(link.id, targetDetailsUrl)}
                        >
                          {isDraftsPage ? "העתק קישור טיוטה" : "העתק קישור"}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="customer-links-mobile-btn-danger"
                        onClick={() => handleDeleteOffer(link)}
                        aria-label="מחק הצעה"
                      >
                        מחק
                      </button>
                    </div>
                    <details className="customer-links-mobile-details">
                      <summary>פרטים נוספים</summary>
                      <dl className="customer-links-mobile-dl customer-links-mobile-dl--extras">
                        <div className="customer-links-mobile-dl__row">
                          <dt>דמי טיפול</dt>
                          <dd>{formatHandlingFeeInline(link)}</dd>
                        </div>
                        <div className="customer-links-mobile-dl__row">
                          <dt>מזהה</dt>
                          <dd>{String(link.id ?? "—")}</dd>
                        </div>
                        {link.deal_type || link.product_type ? (
                          <div className="customer-links-mobile-dl__row">
                            <dt>סוג עסקה (raw)</dt>
                            <dd>{String(link.deal_type || link.product_type)}</dd>
                          </div>
                        ) : null}
                        {link.dealid != null ? (
                          <div className="customer-links-mobile-dl__row">
                            <dt>deal id</dt>
                            <dd>{String(link.dealid)}</dd>
                          </div>
                        ) : null}
                        {link.internal_draft_url ? (
                          <div className="customer-links-mobile-dl__row customer-links-mobile-dl__row--block">
                            <dt>כתובת פנימית</dt>
                            <dd className="customer-links-mobile-longtext">{link.internal_draft_url}</dd>
                          </div>
                        ) : null}
                        {targetDetailsUrl ? (
                          <div className="customer-links-mobile-dl__row customer-links-mobile-dl__row--block">
                            <dt>{isDraftsPage ? "קישור טיוטה לעדכון" : "קישור מלא"}</dt>
                            <dd className="customer-links-mobile-longtext">{targetDetailsUrl}</dd>
                          </div>
                        ) : null}
                      </dl>
                    </details>
                  </article>
                );
              })
            : null}
        </div>
      ) : null}

      {!isMobile ? (
      <div className="customer-links-table-wrapper">
        <table className="customer-links-table">
          <thead>
            <tr>
              <th className="th-agent">סוכן</th>
              {isDraftsPage && <th className="th-status">סטטוס</th>}
              {isDraftsPage && <th className="th-offer-number">מספר הצעה</th>}
              <th className="th-created">תאריך יצירה</th>
              <th className="th-type">סוג מוצר</th>
              <th className="th-hotel">מלון / מסלול</th>
              <th className="th-dates">תאריכים</th>
              <th className="th-dest">יעד</th>
              <th className="th-price">מחיר</th>
              <th className="th-fee">דמי טיפול</th>
              <th className="th-link">קישור</th>
              <th className="th-actions">פעולות</th>
            </tr>
          </thead>
          <tbody>
            {loading && !(Array.isArray(links) && links.length > 0) &&
              Array.from({ length: SKELETON_INITIAL_ROWS }, (_, i) => (
                <tr key={`skeleton-init-${i}`} className="customer-links-skeleton-row" aria-hidden="true">
                  {Array.from({ length: isDraftsPage ? 13 : 11 }, (__, j) => (
                    <td key={j}>
                      <div className="customer-links-skeleton-cell" />
                    </td>
                  ))}
                </tr>
              ))}
            {(backgroundRefreshing || (refreshing && Array.isArray(links) && links.length > 0)) &&
              Array.from({ length: SKELETON_NEW_ROWS }, (_, i) => (
                <tr key={`skeleton-new-${i}`} className="customer-links-skeleton-row" aria-hidden="true">
                  {Array.from({ length: isDraftsPage ? 13 : 11 }, (__, j) => (
                    <td key={j}>
                      <div className="customer-links-skeleton-cell" />
                    </td>
                  ))}
                </tr>
              ))}
            {((!Array.isArray(displayLinks) || displayLinks.length === 0) && !loading && !backgroundRefreshing) ? (
              <tr>
                <td colSpan={isDraftsPage ? 13 : 11} className="empty-state-cell">
                  <div className="empty-state">
                    <p>
                      {isDraftsPage && statusFilter === "open" && Array.isArray(filteredByMonth) && filteredByMonth.length > 0
                        ? "אין הצעות פתוחות בחודש זה"
                        : isDraftsPage && Array.isArray(links) && links.length > 0
                          ? "אין הצעות בחודש שנבחר"
                          : "אין קישורים זמינים"}
                    </p>
                  </div>
                </td>
              </tr>
            ) : null}
            {Array.isArray(displayLinks) && displayLinks.length > 0
              ? (displayLinks.map((link) => {
                const fullUrl = resolveOfferFullUrl(link);
                const offerNumLabel = isDraftsPage
                  ? getDraftOfferNumberLabel(link, offerNumbersByDealId.get(String(link.id)))
                  : null;
                const targetDetailsUrl = isDraftsPage
                  ? resolveDraftPassengerDataUrl(link, fullUrl, location.pathname) || fullUrl
                  : fullUrl;
                if (isDraftsPage) {
                  traceDraftListOpenLink(link, {
                    fullUrl,
                    targetDetailsUrl,
                    offerNumber: offerNumLabel,
                    reason: "draft_open_passenger_data_flow",
                  });
                }
                const isFlightRow = computeIsFlightRow(link);
                const draftOpenUrl = isDraftsPage
                  ? targetDetailsUrl
                  : link.internal_draft_url || fullUrl;
                const continueBookingUrl = isDraftsPage
                  ? targetDetailsUrl
                  : link.continue_booking_url || draftOpenUrl;
                const linkActionTitle = isDraftsPage ? "פתח טיוטה לעדכון" : "פתח קישור";
                return (
                  <tr key={link.id} className="customer-links-row">
                    <td className="td-agent">
                      <span className="cell-primary" title={getOfferOwnerDisplayName(link)}>
                        {getOfferOwnerDisplayName(link)}
                      </span>
                    </td>
                    {isDraftsPage && (
                      <td className="td-status">
                        <select
                          className="draft-status-select"
                          value={draftStatusKey(link)}
                          disabled={statusUpdatingId === link.id}
                          onChange={(e) => handleDraftStatusChange(link, e.target.value)}
                          aria-label="שינוי סטטוס הצעה"
                        >
                          <option value="in_progress">בהליך</option>
                          <option value="paid">שולם</option>
                          <option value="cancelled">בוטל</option>
                        </select>
                      </td>
                    )}
                    {isDraftsPage && (
                      <td className="td-offer-number">
                        <span className="cell-secondary">
                          {offerNumLabel}
                        </span>
                      </td>
                    )}
                    <td className="td-created">
                      <div className="date-block">
                        <span className="cell-muted date-block-main">{formatDate(link.created_at)}</span>
                      </div>
                    </td>
                    <td className="td-type">
                      <span className={`product-type-badge product-type-${getProductTypeClass(link.offer_type === "package_compare" ? "package_compare" : (link.deal_type || link.product_type))}`}>
                        {getProductTypeLabel(link.offer_type === "package_compare" ? "package_compare" : (link.deal_type || link.product_type))}
                      </span>
                    </td>
                    <td className="td-hotel">
                      <div className="draft-hotel-cell">
                        {link.offer_type === "package_compare" ? (
                          <span
                            className="cell-primary"
                            title={tooltipFromList(link.hotels_list)}
                          >
                            {getPackageCompareHotelLabel(link)}
                          </span>
                        ) : (
                          <span className={link.hotel_name ? "cell-primary" : "placeholder-empty"}>
                            {link.hotel_name || "—"}
                          </span>
                        )}
                        {link.room_summary ? (
                          <span className="draft-hotel-room cell-muted" title={link.room_summary}>
                            {link.room_summary}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="td-dates">
                      {link.offer_type === "package_compare" ? (
                        <span
                          className="cell-secondary"
                          title={tooltipFromList(link.dates_list)}
                        >
                          {getPackageCompareDatesLabel(link)}
                        </span>
                      ) : (
                        <div className="dates-block">
                          <div className="dates-block-item">
                            <span className="dates-block-label">יציאה</span>
                            <span className="cell-secondary">{link.dep_date || "—"}</span>
                          </div>
                          <div className="dates-block-item">
                            <span className="dates-block-label">חזרה</span>
                            <span className="cell-secondary">{link.ret_date || "—"}</span>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="td-dest">
                      {link.offer_type === "package_compare" ? (
                        <span
                          className={
                            getPackageCompareDestinationLabel(link) !== "—"
                              ? "cell-secondary"
                              : "placeholder-empty"
                          }
                          title={tooltipFromList(link.destinations_list)}
                        >
                          {getPackageCompareDestinationLabel(link)}
                        </span>
                      ) : (
                        <span
                          className={link.destination ? "cell-secondary" : "placeholder-empty"}
                          title={link.destination ? String(link.destination) : undefined}
                        >
                          {formatDestinationHe(link.destination)}
                        </span>
                      )}
                    </td>
                    <td className="td-price">
                      <LinkRowPriceCell link={link} fxRate={fxRate} />
                    </td>
                    <td className="td-fee">
                      <span className="cell-secondary">{formatHandlingFeeInline(link)}</span>
                    </td>
                    <td className="td-link">
                      <div className="link-cell-compact">
                        {targetDetailsUrl ? (
                          <>
                            <span
                              className="link-truncated"
                              title={targetDetailsUrl}
                              role="button"
                              tabIndex={0}
                              onClick={() => handleOpenLink(targetDetailsUrl)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleOpenLink(targetDetailsUrl);
                                }
                              }}
                            >
                              {targetDetailsUrl.length > 32 ? `${targetDetailsUrl.slice(0, 32)}…` : targetDetailsUrl}
                            </span>
                            <div className="link-actions">
                              <button type="button" className="action-btn-icon action-btn-icon--copy" onClick={() => handleCopyLink(link.id, targetDetailsUrl)} title={isDraftsPage ? "העתק קישור טיוטה" : "העתק קישור"} aria-label={isDraftsPage ? "העתק קישור טיוטה" : "העתק קישור"}><FaCopy /></button>
                              <button type="button" className="action-btn-icon action-btn-icon--open" onClick={() => handleOpenLink(targetDetailsUrl)} title={linkActionTitle} aria-label={linkActionTitle}><FaExternalLinkAlt /></button>
                            </div>
                          </>
                        ) : (
                          <span className="placeholder-empty">אין קישור</span>
                        )}
                      </div>
                    </td>
                    <td className="td-actions">
                      <div className="actions-cell">
                        {isDraftsPage && isFlightRow && draftOpenUrl ? (
                          <button
                            type="button"
                            className="action-btn-text action-btn-text--open"
                            onClick={() => handleOpenLink(targetDetailsUrl)}
                          >
                            פתח טיוטה
                          </button>
                        ) : null}
                        {isDraftsPage && isFlightRow && continueBookingUrl ? (
                          <button
                            type="button"
                            className="action-btn-text action-btn-text--continue"
                            onClick={() => handleOpenLink(targetDetailsUrl)}
                          >
                            המשך להזמנה
                          </button>
                        ) : null}
                        <button type="button" className="action-btn-icon action-btn-icon--delete" onClick={() => handleDeleteOffer(link)} title="מחק הצעה" aria-label="מחק הצעה"><FaTrash /></button>
                      </div>
                    </td>
                  </tr>
                );
              }))
              : null}
          </tbody>
        </table>
      </div>
      ) : null}
    </div>
  );
};

export default CustomerLinksTable;

